/**
 * Staff admin dashboard - served at admin.upstand.ai (host-pinned in App.tsx) and /admin in-app.
 * Two tabs: Usage (cross-workspace analytics) and the referral Leaderboard.
 *
 * All data flows through SECURITY DEFINER RPCs gated on is_staff(auth.uid()) (migration 0108 +
 * 0103) - the nav/host gating is UX; the DB is the security boundary. A non-staff caller gets a
 * 42501 and this page renders the access-denied state.
 *
 * Usage metrics: meetings, recording minutes, transcripts, recaps, check-ins, action items,
 * decisions, Ask Upstand messages, and workspace searches (logged to usage_events from the search
 * palette - tracked from 15 Jul 2026, so earlier ranges legitimately show 0 searches).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  Archive,
  ArchiveRestore,
  BellRing,
  Building2,
  CalendarDays,
  CheckSquare,
  Clock,
  FileText,
  Gauge,
  Laptop,
  ListTodo,
  Mail,
  MessageSquare,
  Search,
  ShieldCheck,
  Sparkles,
  Users2,
  Loader2,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import { supabase } from '@saas-boilerplate/shared';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { LeaderboardPage } from './leaderboard-page';

type Totals = {
  companies: number;
  workspaces: number;
  users: number;
  active_users: number;
  new_users: number;
  meetings: number;
  meetings_bot_enabled: number;
  meetings_recorded: number;
  meetings_no_link: number;
  meetings_desktop: number;
  meetings_desktop_in_person: number;
  meetings_desktop_ready: number;
  meetings_bot_off: number;
  recording_minutes: number;
  transcripts_ready: number;
  recaps_sent: number;
  recaps_skipped: number;
  recap_recipients: number;
  nudges_sent: number;
  nudges_skipped: number;
  nudge_recipients: number;
  check_ins: number;
  action_items: number;
  decisions: number;
  ask_messages: number;
  ask_users: number;
  searches: number;
  search_users: number;
};

type DayRow = {
  day: string;
  meetings: number;
  meetings_recorded: number;
  recording_minutes: number;
  check_ins_text: number;
  check_ins_voice: number;
  check_ins_video: number;
  ask_messages: number;
  searches: number;
};

type CompanyRow = {
  company_key: string;
  company_name: string | null;
  workspaces: number;
  users: number;
  meetings: number;
  recording_minutes: number;
  check_ins: number;
  action_items: number;
  ask_messages: number;
  searches: number;
};

type UserRow = {
  user_id: string;
  full_name: string | null;
  email: string | null;
  company_name: string | null;
  last_active_at: string | null;
  meetings_owned: number;
  check_ins: number;
  action_items_assigned: number;
  ask_messages: number;
  searches: number;
};

type WorkspaceRow = {
  workspace_id: string;
  workspace_name: string | null;
  company_name: string | null;
  active_members: number;
  meetings_30d: number;
  last_active_at: string | null;
  created_at: string | null;
  closed_at: string | null;
};

const RANGES = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last 12 months' },
] as const;

function fmtDay(iso: string): string {
  try {
    return new Date(iso + 'T12:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch {
    return iso;
  }
}

function fmtMinutes(mins: number): string {
  if (!mins) return '0m';
  if (mins < 60) return `${Math.round(mins)}m`;
  return `${Math.round(mins / 60)}h ${Math.round(mins % 60)}m`;
}

function fmtLastActive(iso: string | null): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch {
    return '-';
  }
}

function StatTile({
  icon: Icon,
  label,
  value,
  sub,
  sub2,
}: {
  icon: typeof Activity;
  label: string;
  value: string;
  sub?: string;
  sub2?: string;
}) {
  return (
    <div className="rounded-xl border border-outline-variant/30 bg-white/60 p-4 shadow-sm">
      <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="font-display text-2xl font-semibold tabular-nums text-headline">{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div> : null}
      {sub2 ? <div className="text-xs text-muted-foreground">{sub2}</div> : null}
    </div>
  );
}

export function AdminDashboard({ initialTab = 'usage' }: { initialTab?: 'usage' | 'leaderboard' }) {
  const [rangeDays, setRangeDays] = useState<string>('30');
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [series, setSeries] = useState<DayRow[]>([]);
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [workspacesList, setWorkspacesList] = useState<WorkspaceRow[]>([]);
  const [wsBusy, setWsBusy] = useState<string | null>(null);
  /** Workspace pending the close-confirmation dialog (reopen acts immediately - it's the safe direction). */
  const [pendingClose, setPendingClose] = useState<WorkspaceRow | null>(null);

  const load = useCallback(async (days: number) => {
    const since = new Date(Date.now() - days * 24 * 3600 * 1000).toISOString();
    const until = new Date().toISOString();
    const args = { p_since: since, p_until: until };
    const [t, s, c, u, w] = await Promise.all([
      supabase.rpc('admin_usage_totals', args),
      supabase.rpc('admin_usage_timeseries', args),
      supabase.rpc('admin_usage_by_company', args),
      supabase.rpc('admin_usage_by_user', args),
      supabase.rpc('admin_list_workspaces'),
    ]);
    const firstError = t.error || s.error || c.error || u.error || w.error;
    if (firstError) {
      if ((firstError as { code?: string }).code === '42501' || /not authorized/i.test(firstError.message)) {
        setDenied(true);
      } else {
        toast.error(`Failed to load usage: ${firstError.message}`);
      }
      return;
    }
    setTotals(((t.data as Totals[]) || [])[0] ?? null);
    setSeries((s.data as DayRow[]) || []);
    setCompanies((c.data as CompanyRow[]) || []);
    setUsers((u.data as UserRow[]) || []);
    setWorkspacesList((w.data as WorkspaceRow[]) || []);
  }, []);

  const setWorkspaceClosed = useCallback(
    async (ws: WorkspaceRow, closed: boolean) => {
      setWsBusy(ws.workspace_id);
      const { error } = await supabase.rpc('admin_set_workspace_closed', {
        p_workspace_id: ws.workspace_id,
        p_closed: closed,
      });
      if (error) {
        toast.error(`Couldn't ${closed ? 'close' : 'reopen'} workspace: ${error.message}`);
      } else {
        toast.success(`${ws.workspace_name || 'Workspace'} ${closed ? 'closed' : 'reopened'}.`);
      }
      // Reload everything - closing changes every usage panel, not just the workspace list.
      await load(Number(rangeDays));
      setWsBusy(null);
    },
    [load, rangeDays],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await load(Number(rangeDays));
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [load, rangeDays]);

  const chartData = useMemo(
    () =>
      series.map((d) => ({
        ...d,
        label: fmtDay(d.day),
        meetings: Number(d.meetings || 0),
        meetings_recorded: Number(d.meetings_recorded || 0),
        recording_hours: Math.round((Number(d.recording_minutes || 0) / 60) * 10) / 10,
        ask_messages: Number(d.ask_messages || 0),
        searches: Number(d.searches || 0),
        check_ins_text: Number(d.check_ins_text || 0),
        check_ins_voice: Number(d.check_ins_voice || 0),
        check_ins_video: Number(d.check_ins_video || 0),
      })),
    [series],
  );

  const activeUsersList = useMemo(
    () =>
      users.filter(
        (u) =>
          Number(u.meetings_owned) > 0 ||
          Number(u.check_ins) > 0 ||
          Number(u.action_items_assigned) > 0 ||
          Number(u.ask_messages) > 0 ||
          Number(u.searches) > 0,
      ),
    [users],
  );

  if (denied) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <ShieldCheck className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
        <h1 className="mb-2 font-display text-2xl font-light text-headline">Staff only</h1>
        <p className="text-sm text-muted-foreground">The admin dashboard is only available to Upstand staff.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 lg:px-12 lg:py-12">
      <header className="mb-6">
        <p className="mb-2 flex items-center gap-1.5 font-display text-[10px] font-bold uppercase tracking-[0.25em] text-tertiary">
          <ShieldCheck className="h-3 w-3" /> Staff
        </p>
        <h1 className="mb-2 font-display text-3xl font-light leading-tight tracking-tight text-headline md:text-4xl">
          Admin
        </h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Usage across every company and user, plus the referral leaderboard.
        </p>
      </header>

      <Tabs defaultValue={initialTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="usage" className="gap-1.5">
            <Gauge className="h-3.5 w-3.5" /> Usage
          </TabsTrigger>
          <TabsTrigger value="workspaces" className="gap-1.5">
            <Building2 className="h-3.5 w-3.5" /> Workspaces
          </TabsTrigger>
          <TabsTrigger value="leaderboard" className="gap-1.5">
            <Sparkles className="h-3.5 w-3.5" /> Leaderboard
          </TabsTrigger>
        </TabsList>

        <TabsContent value="usage">
          <div className="mb-5 flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {loading
                ? 'Loading…'
                : `Activity for the selected period. Entity counts are all-time. Closed workspaces are excluded.`}
            </p>
            <Select value={rangeDays} onValueChange={setRangeDays}>
              <SelectTrigger className="h-9 w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading || !totals ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-8">
              {/* Stat tiles */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                <StatTile icon={Building2} label="Companies" value={String(totals.companies)} sub={`${totals.workspaces} workspaces`} />
                <StatTile icon={Users2} label="Users" value={String(totals.users)} sub={`+${totals.new_users} new · ${totals.active_users} active`} />
                <StatTile
                  icon={CalendarDays}
                  label="Meetings held"
                  value={String(totals.meetings_recorded)}
                  sub={(() => {
                    // Dispatchable candidates: bot-enabled occurrences that actually carry a link.
                    const candidates = Math.max(
                      0,
                      Number(totals.meetings_bot_enabled) - Number(totals.meetings_no_link),
                    );
                    const pct =
                      candidates > 0
                        ? Math.round((100 * Number(totals.meetings_recorded)) / candidates)
                        : 0;
                    return `/${candidates} bot-enabled · ${pct}% bot admittance`;
                  })()}
                  sub2={(() => {
                    // Desktop captures are excluded here - they have their own tile.
                    const enrolled = Number(totals.meetings) - Number(totals.meetings_desktop);
                    const parts = [
                      { n: Number(totals.meetings_no_link), label: 'no meeting link' },
                      { n: Number(totals.meetings_bot_off), label: 'bot off' },
                    ]
                      .filter((p) => p.n > 0)
                      .map((p) => `${p.n} ${p.label}`);
                    return `/${enrolled} enrolled${parts.length ? ` · ${parts.join(', ')}` : ''}`;
                  })()}
                />
                <StatTile
                  icon={Laptop}
                  label="Desktop recordings"
                  value={String(totals.meetings_desktop)}
                  sub={`${Number(totals.meetings_desktop) - Number(totals.meetings_desktop_in_person)} meetings · ${totals.meetings_desktop_in_person} in-person`}
                  sub2={`${totals.meetings_desktop_ready} transcripts ready`}
                />
                <StatTile icon={Clock} label="Recording time" value={fmtMinutes(Number(totals.recording_minutes))} sub={`${totals.transcripts_ready} transcripts ready`} />
                <StatTile
                  icon={BellRing}
                  label="Pre-meeting nudges"
                  value={String(totals.nudges_sent)}
                  sub={`${totals.nudge_recipients} recipients · ${totals.nudges_skipped} skipped`}
                />
                <StatTile
                  icon={Mail}
                  label="Recaps sent"
                  value={String(totals.recaps_sent)}
                  sub={`${totals.recap_recipients} recipients · ${totals.recaps_skipped} skipped`}
                />
                <StatTile icon={CheckSquare} label="Check-ins" value={String(totals.check_ins)} />
                <StatTile icon={MessageSquare} label="Ask Upstand" value={String(totals.ask_messages)} sub={`${totals.ask_users} users`} />
                <StatTile icon={Search} label="Searches" value={String(totals.searches)} sub={`${totals.search_users} users · tracked since 15 Jul 2026`} />
                <StatTile icon={ListTodo} label="Action items" value={String(totals.action_items)} />
                <StatTile icon={FileText} label="Decisions" value={String(totals.decisions)} />
              </div>

              {/* Meetings per day */}
              <Card className="border-outline-variant/30">
                <CardHeader>
                  <CardTitle className="font-display text-lg font-semibold text-headline">Meetings per day</CardTitle>
                  <CardDescription>
                    Scheduled = non-cancelled calendar occurrences (includes synced recurring series).
                    Bot recorded = the subset where a bot was admitted and recorded.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -18 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e5e4" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" tickLine={false} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} tickLine={false} axisLine={false} />
                        <RechartsTooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="meetings" name="Scheduled" fill="#d3c4b2" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="meetings_recorded" name="Bot recorded" fill="#795900" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <div className="grid gap-6 lg:grid-cols-2">
                {/* Recording hours per day */}
                <Card className="border-outline-variant/30">
                  <CardHeader>
                    <CardTitle className="font-display text-lg font-semibold text-headline">Recording hours per day</CardTitle>
                    <CardDescription>Bot in-call time by join date.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-56 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e5e4" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" tickLine={false} />
                          <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                          <RechartsTooltip />
                          <Bar dataKey="recording_hours" name="Hours" fill="#e9b32e" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Ask + Search per day */}
                <Card className="border-outline-variant/30">
                  <CardHeader>
                    <CardTitle className="font-display text-lg font-semibold text-headline">Ask &amp; search</CardTitle>
                    <CardDescription>Ask Upstand messages and workspace searches per day.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-56 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e5e4" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" tickLine={false} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} tickLine={false} axisLine={false} />
                          <RechartsTooltip />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Line type="monotone" dataKey="ask_messages" name="Ask" stroke="#795900" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="searches" name="Search" stroke="#8d4f11" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

              </div>

              {/* Check-ins per day, stacked by content type */}
              <Card className="border-outline-variant/30">
                  <CardHeader>
                    <CardTitle className="font-display text-lg font-semibold text-headline">Check-ins per day</CardTitle>
                    <CardDescription>
                      Pre-meeting check-ins by type. (No upload type exists yet - check-ins are typed
                      text, voice, or video.)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-56 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e5e4" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" tickLine={false} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} tickLine={false} axisLine={false} />
                          <RechartsTooltip />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Bar dataKey="check_ins_text" name="Text" stackId="ci" fill="#795900" />
                          <Bar dataKey="check_ins_voice" name="Voice" stackId="ci" fill="#e9b32e" />
                          <Bar dataKey="check_ins_video" name="Video" stackId="ci" fill="#8d4f11" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
              </Card>

              {/* By company */}
              <Card className="border-outline-variant/30">
                <CardHeader>
                  <CardTitle className="font-display text-lg font-semibold text-headline">By company</CardTitle>
                  <CardDescription>Every company (workspaces without a company stand alone), ranked by meetings in the period.</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company</TableHead>
                        <TableHead className="text-right">Users</TableHead>
                        <TableHead className="text-right">Meetings</TableHead>
                        <TableHead className="text-right">Recording</TableHead>
                        <TableHead className="text-right">Check-ins</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                        <TableHead className="text-right">Ask</TableHead>
                        <TableHead className="text-right">Search</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {companies.map((c) => (
                        <TableRow key={c.company_key}>
                          <TableCell className="font-medium text-foreground">{c.company_name || 'Unnamed'}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(c.users)}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(c.meetings)}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmtMinutes(Number(c.recording_minutes))}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(c.check_ins)}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(c.action_items)}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(c.ask_messages)}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(c.searches)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* By user */}
              <Card className="border-outline-variant/30">
                <CardHeader>
                  <CardTitle className="font-display text-lg font-semibold text-headline">By user</CardTitle>
                  <CardDescription>
                    Users with activity in the period ({activeUsersList.length} of {users.length}).
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead className="text-right">Last active</TableHead>
                        <TableHead className="text-right">Meetings owned</TableHead>
                        <TableHead className="text-right">Check-ins</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                        <TableHead className="text-right">Ask</TableHead>
                        <TableHead className="text-right">Search</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeUsersList.map((u) => (
                        <TableRow key={u.user_id}>
                          <TableCell>
                            <div className="font-medium text-foreground">{u.full_name || u.email || 'Unknown'}</div>
                            {u.full_name && u.email ? (
                              <div className="text-xs text-muted-foreground">{u.email}</div>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-muted-foreground">{u.company_name || '-'}</TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">{fmtLastActive(u.last_active_at)}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(u.meetings_owned)}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(u.check_ins)}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(u.action_items_assigned)}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(u.ask_messages)}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(u.searches)}</TableCell>
                        </TableRow>
                      ))}
                      {activeUsersList.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="py-6 text-center text-sm italic text-muted-foreground">
                            No user activity in this period.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="workspaces">
          <Card className="border-outline-variant/30">
            <CardHeader>
              <CardTitle className="font-display text-lg font-semibold text-headline">Workspaces</CardTitle>
              <CardDescription>
                Close test or defunct workspaces to exclude them from every usage panel. Closing is
                reversible and doesn&apos;t affect the workspace&apos;s members or data.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex h-40 items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Workspace</TableHead>
                      <TableHead className="text-right">Members</TableHead>
                      <TableHead className="text-right">Meetings (30d)</TableHead>
                      <TableHead className="text-right">Last active</TableHead>
                      <TableHead className="w-40 text-right">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {workspacesList.map((w) => {
                      const closed = Boolean(w.closed_at);
                      return (
                        <TableRow key={w.workspace_id} className={closed ? 'opacity-60' : undefined}>
                          <TableCell>
                            <div className="font-medium text-foreground">{w.workspace_name || 'Untitled'}</div>
                            {w.company_name ? (
                              <div className="text-xs text-muted-foreground">{w.company_name}</div>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{Number(w.active_members)}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(w.meetings_30d)}</TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">
                            {fmtLastActive(w.last_active_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {closed ? <Badge variant="outline">Closed</Badge> : null}
                              {wsBusy === w.workspace_id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                              ) : null}
                              {closed ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  disabled={wsBusy !== null}
                                  onClick={() => void setWorkspaceClosed(w, false)}
                                >
                                  <ArchiveRestore className="mr-1 h-3.5 w-3.5" /> Reopen
                                </Button>
                              ) : (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                                  disabled={wsBusy !== null}
                                  onClick={() => setPendingClose(w)}
                                >
                                  <Archive className="mr-1 h-3.5 w-3.5" /> Close
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Close confirmation - closing hides the workspace from all usage analytics. */}
          <AlertDialog open={pendingClose !== null} onOpenChange={(o) => !o && setPendingClose(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Close {pendingClose?.workspace_name || 'this workspace'}?</AlertDialogTitle>
                <AlertDialogDescription>
                  It will be excluded from all usage analytics on this dashboard. Members keep access to
                  the workspace itself, and you can reopen it here at any time.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    const ws = pendingClose;
                    setPendingClose(null);
                    if (ws) void setWorkspaceClosed(ws, true);
                  }}
                >
                  Close workspace
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        <TabsContent value="leaderboard">
          {/* Reuse the existing staff leaderboard wholesale (it renders its own header + panels). */}
          <div className="-mx-6 -my-8 lg:-mx-12 lg:-my-12">
            <LeaderboardPage />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
